/*
  rk Hero
  1. Fallback für Parallax/Zoom beim Scrollen in Browsern ohne
     CSS scroll-driven animations. Bei reduzierter Bewegung inaktiv.
  2. Schwebende Kaufleiste: sichtbar, sobald die Hero-Buttons aus dem
     Bild sind; ausgeblendet, solange [data-rk-buybar-hide] oder der
     Footer sichtbar ist.
*/
(() => {
  if (window.RkHero) return;
  window.RkHero = true;

  const supportsTimeline = window.CSS && CSS.supports('animation-timeline: scroll()');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Zoom-Fallback ---------- */
  let zoomItems = [];
  let ticking = false;

  const updateZoom = () => {
    ticking = false;
    const progress = Math.min(Math.max(window.scrollY / window.innerHeight, 0), 1);
    zoomItems.forEach((item) => item.style.setProperty('--rk-hero-progress', progress.toFixed(3)));
  };

  const onScroll = () => {
    if (ticking || !zoomItems.length) return;
    ticking = true;
    requestAnimationFrame(updateZoom);
  };

  const initZoom = () => {
    if (supportsTimeline) return;
    zoomItems = Array.from(document.querySelectorAll('.rk-hero--motion [data-rk-hero-media]'));
    zoomItems.forEach((item) => item.classList.toggle('is-rk-js-zoom', !reducedMotion.matches));
    updateZoom();
  };

  if (!supportsTimeline) window.addEventListener('scroll', onScroll, { passive: true });

  /* ---------- Kaufleiste ---------- */
  let barObserver = null;
  let productObserver = null;

  const initBarProducts = (bar) => {
    productObserver?.disconnect();
    const sources = [...document.querySelectorAll('[data-rk-buybar-product]')];
    const title = bar.querySelector('.rk-buybar__title');
    const price = bar.querySelector('.rk-buybar__price');
    const link = bar.querySelector('a.rk-btn');
    if (!sources.length || !link) return;

    const initial = {
      title: title?.textContent ?? '',
      price: price?.textContent ?? '',
      url: link.getAttribute('href'),
    };
    const active = new Set();
    let current = null;

    const apply = (source) => {
      if (source === current) return;
      current = source;
      const data = source
        ? { title: source.dataset.rkBarTitle, price: source.dataset.rkBarPrice, url: source.dataset.rkBarUrl }
        : initial;
      const swap = () => {
        if (title && data.title) title.textContent = data.title;
        if (price) price.textContent = data.price || '';
        if (data.url) link.setAttribute('href', data.url);
        bar.classList.remove('is-swapping');
      };
      if (reducedMotion.matches || !bar.classList.contains('is-visible')) {
        swap();
      } else {
        bar.classList.add('is-swapping');
        window.setTimeout(swap, 200);
      }
    };

    productObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) active.add(entry.target);
          else active.delete(entry.target);
        });
        apply(sources.find((source) => active.has(source)) ?? null);
      },
      { rootMargin: '-50% 0px -50% 0px' }
    );
    sources.forEach((source) => productObserver.observe(source));
  };

  const initBar = () => {
    barObserver?.disconnect();
    productObserver?.disconnect();
    const bar = document.querySelector('[data-rk-buybar]');
    const actions = document.querySelector('[data-rk-hero-actions]');
    if (!bar || !actions) return;

    const hideZones = [...document.querySelectorAll('[data-rk-buybar-hide], .rk-footer, footer')];
    const state = new Map();
    let passedHero = false;

    const setVisible = (visible) => {
      bar.classList.toggle('is-visible', visible);
      bar.inert = !visible;
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      document.documentElement.classList.toggle('rk-buybar-on', visible);
    };

    bar.hidden = false;
    setVisible(false);

    barObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === actions) {
          passedHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        } else {
          state.set(entry.target, entry.isIntersecting);
        }
      });
      const inHideZone = [...state.values()].some(Boolean);
      setVisible(passedHero && !inHideZone);
    });

    barObserver.observe(actions);
    hideZones.forEach((zone) => barObserver.observe(zone));
    initBarProducts(bar);
  };

  const init = () => {
    initZoom();
    initBar();
  };

  reducedMotion.addEventListener?.('change', initZoom);
  document.addEventListener('shopify:section:load', init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
